import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { CardPage } from '../../types.ts'

export interface AudioHandle {
  seek(ms: number): void
}

interface AudioPlayerProps {
  audio: CardPage['audio']
  /** The signed link expired (the engine answers 401/403): re-read the card once. */
  onExpired: () => Promise<unknown>
}

/**
 * The one audio path: `GET /api/audits/:id/attachments/:att/audio?token=…`,
 * console-facing, minted with the card. A failure to load refetches the
 * card once for a fresh link and then stops.
 */
export const AudioPlayer = forwardRef<AudioHandle, AudioPlayerProps>(function AudioPlayer(
  { audio, onExpired },
  ref,
) {
  const { t } = useTranslation()
  const element = useRef<HTMLAudioElement>(null)
  const [retried, setRetried] = useState(false)
  const file = audio[0] ?? null

  useImperativeHandle(ref, () => ({
    seek(ms) {
      const node = element.current
      if (!node) return
      node.currentTime = ms / 1000
      void node.play().catch(() => undefined)
    },
  }))

  if (file === null || file.url === null) {
    return (
      <p className="text-xs text-muted">
        {file === null
          ? t('pages.audits.card.audio.none', { defaultValue: 'No recording for this call.' })
          : file.erasedAt !== null
            ? t('pages.audits.card.audio.erased', {
                defaultValue: 'Audio deleted at the subject’s request.',
              })
            : t('pages.audits.card.audio.noLink', {
                defaultValue:
                  'The instance mints no signed links; the recording stays on the volume.',
              })}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      <audio
        ref={element}
        controls
        preload="metadata"
        src={file.url}
        className="w-full"
        onError={() => {
          if (retried) return
          setRetried(true)
          void onExpired()
        }}
      >
        {t('pages.audits.card.audio.unsupported', {
          defaultValue: 'Your browser cannot play this recording.',
        })}
      </audio>
      {retried && (
        <p className="text-[11px] text-muted">
          {t('pages.audits.card.audio.refetched', {
            defaultValue: 'The link had expired; a fresh one was fetched once.',
          })}
        </p>
      )}
    </div>
  )
})
